/* ═══════════════════════════════════════════════════════════════
   MASTER → VARIANT UPDATE FEED — the one brain (Phase 3,
   docs/MASTERS_UNIFIED_PLAN.md §"PR feed, master → variant").

   When the office master publishes an edit, every client variant gets a
   GitHub-PR-style feed entry: the block-level diff of the master, read
   AGAINST that variant's own (possibly deviated) copy, with an
   accept / reject decision per change.

   Two pure halves, both unit-tested (`variant-updates.test.ts`) and both
   reachable from the renderer AND the API server (esbuild bundles this
   file to ec2-api/shared-variant-updates.js — same trick as the
   permission catalog, so the accept/reject maths can never drift between
   the client that draws the card and the server that persists it):

     1. GENERATION — `buildVariantChangeEntries()` at publish time.
     2. DECISION   — `applyDecisionToLayer()` turns one accept/reject into
        the layer mutation it means.

   ── The semantics (locked — docs/MODULES_VISION.md "Propagation = PR,
   never silent mutation") ─────────────────────────────────────────
   A master publish is OFFERED to every variant, never applied. At publish
   time `holdLayerForChanges()` freezes each variant at the words it had
   (a hold override on a modified block, a hold hide on an added block), so
   nothing in a variant moves until someone answers the feed:

     · modified block, no override   HELD at the previous words (override
                                     based on the OLD core → reads stale)
                                     ACCEPT = drop the hold, take the core
                                     REJECT = keep the words, re-base onto
                                              the new core (a knowing
                                              deviation, no longer stale)
     · added block, no override      HELD out (hide)
                                     ACCEPT = un-hide, take the block
                                     REJECT = stays hidden (a ghost the
                                              variant can restore)
     · overridden block, modified    ACCEPT = drop the override (take core)
                                     REJECT = keep it, re-base onto the new
                                              core so the stale flag clears
     · removed block                 ACCEPT = let it go (and drop a now-
                                     orphan override)
                                     REJECT = resurrect it as a layer add at
                                              the nearest surviving anchor,
                                              carrying the override's words
                                              when there was one
     · block the variant HIDES       auto-accepted at generation (hiding
                                     already won — the change is recorded so
                                     the history is honest, never asked about)

   Nothing here touches `coreContent`: a decision only ever rewrites the
   client's own layer.
   ═══════════════════════════════════════════════════════════════ */

import type { BlockOverride, ClientLayer, ContentBlock, LayerAdd } from './spec';
import { buildInlineDiff } from './block-diff';
import { hashBlock, makeOverrideBase, stableStringify } from './spec-compose';

/* ─── Shapes ─────────────────────────────────────────────────── */

export type VariantChangeKind = 'added' | 'removed' | 'modified';
export type VariantChangeDecision = 'pending' | 'accepted' | 'rejected';
export type VariantChangeEntryStatus = 'pending' | 'resolved';

/** One block-level change in the master, seen from one variant. */
export interface VariantChange {
  changeId: string;
  blockId: string;
  kind: VariantChangeKind;
  /** The block as the previously-published master had it. Absent on 'added'. */
  beforeBlock?: ContentBlock;
  /** The block as the newly-published master has it. Absent on 'removed'. */
  afterBlock?: ContentBlock;
  /** Where this sits in the document — the nearest heading above it. The
   *  printed paragraph number needs a Forma and the renderer's numbering
   *  engine, so the feed carries a location the reader recognises instead. */
  ref: string;
  excerpt: string;
  /** This variant had its own words on the block when the master moved. */
  hadOverride: boolean;
  /** This variant hides the block — the change can never reach it. */
  hadHide: boolean;
  /** For 'removed': the block to anchor a resurrection after, chosen as the
   *  nearest preceding block that SURVIVED the publish. Absent = document top. */
  anchorAfterId?: string;
  /** MODULE updates only (Phase 6, `module-updates.ts`): when nothing before
   *  the new block survived, the block it goes BEFORE instead. */
  anchorBeforeId?: string;
  /** MODULE updates only: which insertion of the module this change belongs
   *  to. Two drops of one module answer separately. */
  insertId?: string;
  /** MODULE updates only: the op names a block this copy no longer has, so
   *  there is nothing to apply. Arrives already settled, with a note. */
  unmappable?: boolean;
  decision: VariantChangeDecision;
  decidedBy?: string;
  decidedAt?: string;
  /** Why a change arrived already decided (hidden blocks). */
  note?: string;
}

/** One publish, one variant: the feed row.
 *
 *  Phase 6 reuses the same row for MODULE updates. Those carry `moduleId`
 *  (the flat `module_*` columns of migration 048, named exactly as they are
 *  stored so nothing has to reshape them), and their `layerId` is NULL when
 *  the entry targets the office master's own core content. */
export interface VariantChangeEntry {
  id: string;
  firmId?: string;
  specMasterId: string;
  /** The `ClientLayer.id` this entry belongs to — NULL on a module update
   *  aimed at the office master itself. */
  layerId: string | null;
  /** The client behind that layer, denormalised for the panel header. */
  clientId?: string;
  fromVersion: number;
  toVersion: number;
  createdAt?: string;
  createdBy?: string;
  changes: VariantChange[];
  status: VariantChangeEntryStatus;

  /* ─── Module updates (Phase 6) ─── */
  /** Set ⇒ this entry is a MODULE definition update, not a master publish. */
  moduleId?: string;
  /** The module's name when the update was raised. */
  moduleName?: string;
  moduleFromVersion?: number;
  moduleToVersion?: number;
  /** The definition diff the entry was built from — kept so the server can
   *  re-read the old ladder without the library (which has already moved on). */
  defDiff?: import('./spec-modules').ModuleDefDiff;

  /* ─── Local module updates (R2 Phase 3, `module-updates.ts`) ─── */
  /** DERIVED, never persisted as a feed row: this entry was computed in the
   *  OPEN document against the CURRENT library definition, not raised by a
   *  definition change. Its answers rewrite the document itself and are saved
   *  with the draft like any other edit. */
  local?: boolean;
  /** Local updates only: the insertion this entry is about. */
  insertId?: string;
  /** Local updates only: this copy predates per-block traceback
   *  (`sourceBlockId`), so the only honest answer is whole-module take/keep. */
  wholeModuleOnly?: boolean;
}

/** Is this feed row a module update rather than a master publish? */
export const isModuleEntry = (entry: Pick<VariantChangeEntry, 'moduleId'> | undefined): boolean =>
  !!entry && !!entry.moduleId;

export interface IdContext {
  now?: string;
  by?: string;
  newId?: (prefix: string) => string;
}

function defaultId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** Exported for `module-updates.ts` — the Phase 6 half of this feed mints its
 *  ids and timestamps through the SAME injectable context, so a test can be
 *  deterministic on both halves at once. */
export const idOf = (ctx: IdContext | undefined, prefix: string): string =>
  (ctx?.newId || defaultId)(prefix);

export const nowOf = (ctx: IdContext | undefined): string => ctx?.now || new Date().toISOString();

/* ─── Generation ─────────────────────────────────────────────── */

const EXCERPT_LEN = 90;

/** The same one-line summary the issuance addendum prints. */
export function excerptOf(block: Partial<ContentBlock> | undefined): string {
  if (!block) return '';
  if (block.blockType === 'table') return '[table]';
  if (block.blockType === 'image') return '[figure]';
  const t = (block.text || '').trim();
  return t.length > EXCERPT_LEN ? t.slice(0, EXCERPT_LEN - 1).trimEnd() + '…' : t;
}

/** Nearest heading (level 1–2) at or above `index`, for the card's "where".
 *  Shared with the module-update half (Phase 6) so every card in the panel
 *  names its place the same way. */
export function refFor(blocks: ContentBlock[], index: number): string {
  for (let i = Math.min(index, blocks.length - 1); i >= 0; i--) {
    const b = blocks[i];
    if (!b) continue;
    if ((b.level || 9) <= 2 && (b.text || '').trim()) {
      const t = b.text.trim();
      return t.length > 60 ? t.slice(0, 59).trimEnd() + '…' : t;
    }
  }
  return '';
}

/**
 * The block-level diff of the master itself: which blocks were added,
 * removed or genuinely changed between two published versions.
 *
 * Rides the sync engine (SYNC_PLAN D5): `buildInlineDiff` takes its block
 * adds / removes from `sync/merge3` and its words from jsdiff.
 * `buildInlineDiff` walks the ids (so a move is not a rewrite) and compares
 * text + level; a block whose TABLE, image or emphasis changed reads as
 * "unchanged" there, so every unchanged row is re-tested with the compose
 * brain's content hash — the same yardstick that decides staleness.
 */
export function diffCore(
  prevCore: ContentBlock[],
  nextCore: ContentBlock[]
): Array<{ blockId: string; kind: VariantChangeKind }> {
  const prevById = new Map((prevCore || []).map((b) => [b.id, b]));
  const nextById = new Map((nextCore || []).map((b) => [b.id, b]));
  const out: Array<{ blockId: string; kind: VariantChangeKind }> = [];
  for (const row of buildInlineDiff(prevCore || [], nextCore || [])) {
    if (row.kind === 'unchanged') {
      const before = prevById.get(row.id);
      const after = nextById.get(row.id);
      if (before && after && hashBlock(before) !== hashBlock(after)) {
        out.push({ blockId: row.id, kind: 'modified' });
      }
      continue;
    }
    out.push({ blockId: row.id, kind: row.kind });
  }
  return out;
}

/** The changes ONE variant sees, annotated with what that variant had done
 *  to each block. Empty when the publish cannot touch this variant at all. */
export function buildVariantChanges(
  prevCore: ContentBlock[],
  nextCore: ContentBlock[],
  layer: ClientLayer,
  ctx?: IdContext
): VariantChange[] {
  const prev = prevCore || [];
  const next = nextCore || [];
  const prevById = new Map(prev.map((b) => [b.id, b]));
  const nextById = new Map(next.map((b) => [b.id, b]));
  const prevIndex = new Map(prev.map((b, i) => [b.id, i]));
  const nextIndex = new Map(next.map((b, i) => [b.id, i]));
  const survives = new Set(next.map((b) => b.id));
  const overridden = new Set((layer.overrides || []).map((o) => o.blockId));
  const hidden = new Set(layer.hides || []);

  const out: VariantChange[] = [];
  for (const d of diffCore(prev, next)) {
    const beforeBlock = prevById.get(d.blockId);
    const afterBlock = nextById.get(d.blockId);
    const hadOverride = overridden.has(d.blockId);
    const hadHide = hidden.has(d.blockId);

    let anchorAfterId: string | undefined;
    if (d.kind === 'removed') {
      const at = prevIndex.get(d.blockId) ?? 0;
      for (let i = at - 1; i >= 0; i--) {
        if (survives.has(prev[i].id)) {
          anchorAfterId = prev[i].id;
          break;
        }
      }
    }

    const ref =
      d.kind === 'removed'
        ? refFor(prev, prevIndex.get(d.blockId) ?? 0)
        : refFor(next, nextIndex.get(d.blockId) ?? 0);

    out.push({
      changeId: idOf(ctx, 'chg'),
      blockId: d.blockId,
      kind: d.kind,
      ...(beforeBlock ? { beforeBlock } : {}),
      ...(afterBlock ? { afterBlock } : {}),
      ref,
      excerpt: excerptOf(afterBlock || beforeBlock),
      hadOverride,
      hadHide,
      ...(anchorAfterId ? { anchorAfterId } : {}),
      // Hiding already won: the block never reaches this client, so there is
      // nothing to decide. Recorded (not dropped) so the history is honest.
      decision: hadHide ? 'accepted' : 'pending',
      ...(hadHide
        ? {
            decidedAt: nowOf(ctx),
            note: 'Accepted automatically — this block is hidden in this variant.',
          }
        : {}),
    });
  }
  return out;
}

/** Is every change in this entry decided? */
export function isEntryResolved(entry: Pick<VariantChangeEntry, 'changes'>): boolean {
  return (entry.changes || []).every((c) => c.decision !== 'pending');
}

/** How many changes still need an answer. Drives every badge in the UI. */
export function pendingCount(entry: Pick<VariantChangeEntry, 'changes'>): number {
  return (entry.changes || []).filter((c) => c.decision === 'pending').length;
}

/**
 * One publish → one feed row per variant that the publish actually touches.
 * A master with no layers, or a publish with no block-level change, produces
 * nothing at all.
 */
export function buildVariantChangeEntries(args: {
  specMasterId: string;
  fromVersion: number;
  toVersion: number;
  prevCore: ContentBlock[];
  nextCore: ContentBlock[];
  layers: ClientLayer[];
  createdBy?: string;
  ctx?: IdContext;
}): VariantChangeEntry[] {
  const { specMasterId, fromVersion, toVersion, prevCore, nextCore, layers, createdBy, ctx } = args;
  const entries: VariantChangeEntry[] = [];
  for (const layer of layers || []) {
    const changes = buildVariantChanges(prevCore, nextCore, layer, ctx);
    if (!changes.length) continue;
    entries.push({
      id: idOf(ctx, 'vcu'),
      specMasterId,
      layerId: layer.id,
      ...(layer.clientId ? { clientId: layer.clientId } : {}),
      fromVersion,
      toVersion,
      createdAt: nowOf(ctx),
      ...(createdBy ? { createdBy } : {}),
      changes,
      status: isEntryResolved({ changes }) ? 'resolved' : 'pending',
    });
  }
  return entries;
}

/* ─── Decisions ──────────────────────────────────────────────── */

/** Stamp one change's decision and re-derive the entry's status. */
export function decideChange(
  entry: VariantChangeEntry,
  changeId: string,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): VariantChangeEntry {
  const changes = (entry.changes || []).map((c) =>
    c.changeId !== changeId
      ? c
      : {
          ...c,
          decision,
          decidedAt: nowOf(ctx),
          ...(ctx?.by ? { decidedBy: ctx.by } : {}),
        }
  );
  return { ...entry, changes, status: isEntryResolved({ changes }) ? 'resolved' : 'pending' };
}

/** Answer every still-pending change the same way (Accept all / Reject all).
 *  Already-decided changes are never re-opened. */
export function decideAll(
  entry: VariantChangeEntry,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): VariantChangeEntry {
  const changes = (entry.changes || []).map((c) =>
    c.decision !== 'pending'
      ? c
      : {
          ...c,
          decision,
          decidedAt: nowOf(ctx),
          ...(ctx?.by ? { decidedBy: ctx.by } : {}),
        }
  );
  return { ...entry, changes, status: isEntryResolved({ changes }) ? 'resolved' : 'pending' };
}

/** The content fields an override carries — the SAME set the compose brain
 *  hashes, so a payload can never disagree with staleness. */
function payloadOf(block: ContentBlock): BlockOverride['payload'] {
  return makeOverrideBase(block).fields;
}

function withOverride(layer: ClientLayer, override: BlockOverride): ClientLayer {
  const list = layer.overrides || [];
  const at = list.findIndex((o) => o.blockId === override.blockId);
  const next = at >= 0 ? list.map((o, i) => (i === at ? override : o)) : [...list, override];
  return { ...layer, overrides: next };
}

function withoutOverride(layer: ClientLayer, blockId: string): ClientLayer {
  return { ...layer, overrides: (layer.overrides || []).filter((o) => o.blockId !== blockId) };
}

/**
 * Turn one decision into the layer it means. PURE — hand it the layer, get a
 * new layer back; the caller persists it. Never touches core content, never
 * touches another variant.
 */
export function applyDecisionToLayer(
  layer: ClientLayer,
  change: VariantChange,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): ClientLayer {
  const now = nowOf(ctx);
  const stamp = (o: BlockOverride): BlockOverride => ({
    ...o,
    updatedAt: now,
    ...(ctx?.by ? { updatedBy: ctx.by } : {}),
  });
  const existing = (layer.overrides || []).find((o) => o.blockId === change.blockId);

  // A hidden block cannot be reached by a core edit — the entry records the
  // change, the layer stays exactly as it was.
  if (change.hadHide) return layer;

  if (change.kind === 'removed') {
    // ACCEPT: the block is gone for everyone. Drop an override that now
    // points at nothing so the variant carries no orphan.
    const isHold = (a: LayerAdd): boolean =>
      !!a.heldFromRemoval &&
      (a.heldFromRemoval === change.changeId || a.block.id === change.blockId);
    const held = (layer.adds || []).some(isHold);
    if (decision === 'accepted') {
      // Drop the kept ghost (C3 hold) as well, so the block really disappears.
      const trimmed = held
        ? { ...layer, adds: (layer.adds || []).filter((a) => !isHold(a)) }
        : layer;
      return existing ? withoutOverride(trimmed, change.blockId) : trimmed;
    }
    // REJECT on a held block: it is already in place — just clear the marker so
    // it reads as an ordinary variant add.
    if (held) {
      const cleared: ClientLayer = {
        ...layer,
        adds: (layer.adds || []).map((a) => {
          if (!isHold(a)) return a;
          const rest: LayerAdd = { ...a };
          delete rest.heldFromRemoval;
          return rest;
        }),
      };
      // The held add already carries the override's words — drop the orphan.
      return existing ? withoutOverride(cleared, change.blockId) : cleared;
    }
    // REJECT: keep the paragraph. It becomes this client's OWN block —
    // carrying their words when they had overridden it, the master's
    // otherwise — anchored after the nearest block that survived.
    const base = change.beforeBlock;
    if (!base) return layer;
    const revived: ContentBlock = existing
      ? ({ ...base, ...existing.payload, id: base.id } as ContentBlock)
      : base;
    const add: LayerAdd = {
      block: revived,
      ...(change.anchorAfterId ? { afterBlockId: change.anchorAfterId } : { index: 0 }),
    };
    const adds = [...(layer.adds || []), add];
    const cleaned = existing ? withoutOverride(layer, change.blockId) : layer;
    return { ...cleaned, adds };
  }

  // ── added / modified ──
  const core = change.afterBlock;
  if (!core) return layer;

  if (existing) {
    // ACCEPT: take the office master's new words — the override goes.
    if (decision === 'accepted') return withoutOverride(layer, change.blockId);
    // REJECT: keep this client's words, re-based onto the new core so the
    // "core has changed" flag clears. Reviewed and declined, not ignored.
    return withOverride(layer, stamp({ ...existing, base: makeOverrideBase(core) }));
  }

  // No override. A block the master ADDED was HELD out (hidden) at publish:
  // ACCEPT releases the hold, REJECT leaves it out (a ghost the variant can
  // restore).
  if (change.kind === 'added') {
    const hides = layer.hides || [];
    if (decision === 'accepted') {
      return hides.includes(change.blockId)
        ? { ...layer, hides: hides.filter((id) => id !== change.blockId) }
        : layer;
    }
    return hides.includes(change.blockId) ? layer : { ...layer, hides: [...hides, change.blockId] };
  }

  // A modified block whose hold was already released (or never placed):
  // accepting means the core text flows, nothing to write.
  if (decision === 'accepted') return layer;

  // REJECT on a modified block: pin the words as they were, based on the NEW
  // core so this reads as a deliberate deviation, never as a stale override.
  const before = change.beforeBlock;
  if (!before) return layer;
  return withOverride(
    layer,
    stamp({
      id: idOf(ctx, 'ov'),
      blockId: change.blockId,
      payload: payloadOf(before),
      base: makeOverrideBase(core),
      createdAt: now,
    })
  );
}

/**
 * HOLD — the other half of "offered, never applied". Called at publish time
 * with the entry just generated for this variant: every pending change on a
 * block the variant had not touched gets a hold, so the variant keeps reading
 * exactly as it did until the feed is answered. PURE — the caller persists.
 *
 *   modified, no override → hold override carrying the PREVIOUS words, based
 *                            on the previous core (so compose() flags it
 *                            stale = "the master moved under this")
 *   added, no override    → hold hide (the new block waits as a ghost)
 *   everything else       → untouched (overrides already hold their own
 *                            words; removed blocks are HELD as a kept add (heldFromRemoval);
 *                            hidden blocks were auto-accepted)
 */
export function holdLayerForChanges(
  layer: ClientLayer,
  changes: VariantChange[],
  ctx?: IdContext
): ClientLayer {
  let next = layer;
  const now = nowOf(ctx);
  for (const change of changes || []) {
    if (change.decision !== 'pending' || change.hadHide) continue;
    if (change.hadOverride && change.kind !== 'removed') continue;
    if (change.kind === 'modified' && change.beforeBlock) {
      // Idempotent: a hold already in place (the renderer held, the server
      // re-runs it) is left exactly as it is.
      const base = makeOverrideBase(change.beforeBlock);
      const payload = payloadOf(change.beforeBlock);
      const have = (next.overrides || []).find((o) => o.blockId === change.blockId);
      if (
        have &&
        have.base?.hash === base.hash &&
        stableStringify(have.payload) === stableStringify(payload)
      ) {
        continue;
      }
      next = withOverride(next, {
        id: idOf(ctx, 'ov'),
        blockId: change.blockId,
        payload,
        base,
        createdAt: now,
        ...(ctx?.by ? { updatedBy: ctx.by } : {}),
      });
    } else if (change.kind === 'added') {
      const hides = next.hides || [];
      if (!hides.includes(change.blockId)) next = { ...next, hides: [...hides, change.blockId] };
    } else if (change.kind === 'removed' && change.beforeBlock) {
      // A removed core block stays in this variant as a kept ghost until answered.
      const adds = next.adds || [];
      // Idempotent by BLOCK, not change id: the server re-running the hold
      // mints its own ids. An add already carrying the block is re-marked as
      // this change's hold so Accept can still let it go.
      const sameBlock = adds.findIndex((a) => a.block && a.block.id === change.blockId);
      if (sameBlock >= 0) {
        if (!adds[sameBlock].heldFromRemoval) {
          next = {
            ...next,
            adds: adds.map((a, i) =>
              i === sameBlock ? { ...a, heldFromRemoval: change.changeId } : a
            ),
          };
        }
      } else if (!adds.some((a) => a.heldFromRemoval === change.changeId)) {
        // A block the variant had OVERRIDDEN is held with the override's words.
        const ov = change.hadOverride
          ? (next.overrides || []).find((o) => o.blockId === change.blockId)
          : undefined;
        const add: LayerAdd = {
          block: ov
            ? ({ ...change.beforeBlock, ...ov.payload, id: change.beforeBlock.id } as ContentBlock)
            : change.beforeBlock,
          ...(change.anchorAfterId ? { afterBlockId: change.anchorAfterId } : { index: 0 }),
          heldFromRemoval: change.changeId,
        };
        next = { ...next, adds: [...adds, add] };
      }
    }
  }
  return next;
}

/** Apply the hold to every layer an entry set touches. Layers without an
 *  entry come back as they were. */
export function holdLayersForEntries(
  layers: ClientLayer[],
  entries: VariantChangeEntry[],
  ctx?: IdContext
): ClientLayer[] {
  const byLayer = new Map((entries || []).map((e) => [e.layerId, e]));
  return (layers || []).map((layer) => {
    const entry = byLayer.get(layer.id);
    return entry ? holdLayerForChanges(layer, entry.changes || [], ctx) : layer;
  });
}

const overrideOn = (layer: ClientLayer | undefined, blockId: string): BlockOverride | undefined =>
  (layer?.overrides || []).find((o) => o.blockId === blockId);

/** Is this override the variant's OWN wording — not a hold at the previous
 *  words, and not the master's new words dressed up as an override? */
function isOwnWording(o: BlockOverride | undefined, change: VariantChange): boolean {
  if (!o) return false;
  const reads = (b: ContentBlock | undefined): boolean =>
    !!b && hashBlock({ ...b, ...(o.payload || {}) } as ContentBlock) === hashBlock(b);
  return !reads(change.beforeBlock) && !reads(change.afterBlock);
}

/**
 * PUBLISH ENFORCEMENT (Tweaks 35, docs/MODULES_VISION.md "Propagation = PR,
 * never silent mutation"). The ONE function both the renderer's publish and
 * the API server's publish batch run, so no client can skip it:
 *
 *   · every block the publish moves reaches every variant as a PENDING
 *     offer — nothing is decided at publish time except a block the variant
 *     hides (hiding already won). A decision a client sent along is
 *     re-opened: the publisher never answers for a variant.
 *   · the variant's words are HELD until someone answers in the variant
 *     (holdLayerForChanges): changed / added / removed alike, with or
 *     without an override.
 *
 * `entries` (optional) are the rows the client raised — their ids and
 * annotations are kept when they describe the real diff; any change the real
 * diff has and the client left out is added. `beforeLayers` are the layers as
 * PUBLISHED before this write: an override a client dropped while "deciding"
 * is put back from there so the variant keeps its own words. PURE.
 */
export function enforcePublishHold(args: {
  specMasterId: string;
  fromVersion: number;
  toVersion: number;
  prevCore: ContentBlock[];
  nextCore: ContentBlock[];
  layers: ClientLayer[];
  beforeLayers?: ClientLayer[];
  entries?: VariantChangeEntry[];
  createdBy?: string;
  ctx?: IdContext;
}): { layers: ClientLayer[]; entries: VariantChangeEntry[] } {
  const { specMasterId, fromVersion, toVersion, ctx } = args;
  const layers = args.layers || [];
  const fresh = buildVariantChangeEntries({
    specMasterId,
    fromVersion,
    toVersion,
    prevCore: args.prevCore || [],
    nextCore: args.nextCore || [],
    layers,
    ...(args.createdBy ? { createdBy: args.createdBy } : {}),
    ctx,
  });
  const sent = (args.entries || []).filter(
    (e) => e && e.specMasterId === specMasterId && !isModuleEntry(e) && e.layerId
  );
  const outLayers: ClientLayer[] = [];
  const outEntries: VariantChangeEntry[] = [];
  for (const layer of layers) {
    const real = fresh.find((e) => e.layerId === layer.id);
    if (!real) {
      outLayers.push(layer);
      continue;
    }
    const theirs = sent.find((e) => e.layerId === layer.id);
    const hides = new Set(layer.hides || []);
    const before = (args.beforeLayers || []).find((l) => l && l.id === layer.id);
    // Every annotation is OURS — read from the real diff, the incoming layer
    // and the layers as stored. The client contributes its ids, nothing else
    // (a client claiming "had an override" must not skip the hold).
    const changes: VariantChange[] = real.changes.map((r) => {
      const mine = (theirs?.changes || []).find(
        (x) => x.blockId === r.blockId && x.kind === r.kind
      );
      const changeId = mine?.changeId || r.changeId;
      const hadOverride =
        r.kind !== 'added' &&
        (isOwnWording(overrideOn(layer, r.blockId), r) ||
          isOwnWording(overrideOn(before, r.blockId), r));
      // A hide on a block the master just ADDED is a hold, never the
      // variant's: only existing blocks can arrive hidden (auto-accepted).
      const hadHide = r.kind !== 'added' && hides.has(r.blockId);
      const out: VariantChange = { ...r, changeId, hadOverride, hadHide, decision: 'pending' };
      delete out.decidedAt;
      delete out.decidedBy;
      delete out.note;
      if (hadHide) {
        return {
          ...out,
          decision: 'accepted',
          decidedAt: nowOf(ctx),
          note: 'Accepted automatically — this block is hidden in this variant.',
        };
      }
      return out;
    });
    // An override a client dropped while answering for the variant comes back.
    let held = layer;
    for (const c of changes) {
      if (c.decision !== 'pending' || !c.hadOverride || c.kind === 'added') continue;
      if (isOwnWording(overrideOn(held, c.blockId), c)) continue;
      const ov = overrideOn(before, c.blockId);
      if (ov && isOwnWording(ov, c)) held = withOverride(held, ov);
    }
    held = holdLayerForChanges(held, changes, ctx);
    outLayers.push(held);
    outEntries.push({
      ...real,
      ...(theirs ? { id: theirs.id } : {}),
      changes,
      status: isEntryResolved({ changes }) ? 'resolved' : 'pending',
    });
  }
  return { layers: outLayers, entries: outEntries };
}

/** Everything one decision changes: the feed row AND the layer. The renderer
 *  and the API server both call this, so they can never disagree. */
export function applyDecision(
  entry: VariantChangeEntry,
  layer: ClientLayer,
  changeId: string,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): { entry: VariantChangeEntry; layer: ClientLayer } {
  const change = (entry.changes || []).find((c) => c.changeId === changeId);
  if (!change || change.decision !== 'pending') return { entry, layer };
  return {
    entry: decideChange(entry, changeId, decision, ctx),
    layer: applyDecisionToLayer(layer, change, decision, ctx),
  };
}

/** Accept all / Reject all — every pending change in one entry, in order. */
export function applyDecisionAll(
  entry: VariantChangeEntry,
  layer: ClientLayer,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): { entry: VariantChangeEntry; layer: ClientLayer } {
  let nextLayer = layer;
  for (const change of entry.changes || []) {
    if (change.decision !== 'pending') continue;
    nextLayer = applyDecisionToLayer(nextLayer, change, decision, ctx);
  }
  return { entry: decideAll(entry, decision, ctx), layer: nextLayer };
}

/** Pending changes for one variant, keyed by block — what the canvas paints
 *  and what the block menu offers. */
export function pendingByBlock(entries: VariantChangeEntry[]): Map<string, VariantChange> {
  const out = new Map<string, VariantChange>();
  for (const entry of entries || []) {
    for (const change of entry.changes || []) {
      if (change.decision !== 'pending') continue;
      // Newest publish wins the block — that is the one the reader answers.
      out.set(change.blockId, change);
    }
  }
  return out;
}
